(ns app.front.view.attachment.upload
  ;; kondo vs cljfmt and uppercase chars
  {:clj-kondo/config {:linters {:unsorted-required-namespaces {:level :off}}}}
  (:require [ajax.core :as ajax :refer [PUT]]
            [app.domain.attachment.file-formats :as file-formats]
            [app.front.entities :as entities]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.yes-no-radio-buttons :as yes-no]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.attachment.common :as attachment-common]
            [app.front.view.bond.common :as-alias bond-common]
            [app.front.view.bond.subs :as bond-subs]
            [app.front.view.property.subs :as property-subs]
            [app.front.view.workspace.subs :as ws-subs]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.comm :as comm]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [goog.crypt.base64 :as base64]
            [goog.crypt.Md5]
            [potpuri.core :as p]
            [re-chain.core :as rc]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]
            [schema-tools.core :as st]))

;; Helper functions
;; ================


(defn valid-attachment?
  [att]
  (or (and (:attachment-id att)
           (-> att :create-as-new? not))
      (and (seq (:attachment/types att))
           (:file-id att))))

(defn set-valid-attachment [att]
  (assoc att :valid? (valid-attachment? att)))

(def file-element-id "file-upload-input")

(defn file-version-element-id [att-id]
  (str "file-version-upload-" att-id))

(defn open-filepicker
  ([e]
   (open-filepicker file-element-id e))
  ([element e]
   (.preventDefault e)
   (-> (.getElementById js/document element)
       .click)))

(defn md5 [bytes]
  (-> (doto (goog.crypt.Md5.)
        (.update bytes))
      .digest
      base64/encodeByteArray))

(defn process-file-read [target-id file event]
  (let [md5-base64 (-> event .-target .-result js/Uint8Array. md5)]
    (>evt [::file-added target-id {:filename         (.-name file)
                                   :size             (.-size file)
                                   :content-type     (.-type file)
                                   :md5-base64       md5-base64
                                   :file             file
                                   :upload-progress  0
                                   :file-id          nil
                                   :attachment/types []}])))

(defn read-files [target-id files]
  (>evt [::set-file-read-in-progress true])
  ;; This reads all the files completely to memory at the same time, might have to consider reading in chunks
  (let [mimes             (->> files
                               (map #(keyword (.-type %)))
                               distinct)
        ;; Zip file cannot be uploaded at the same time with other file types
        conflicting-mimes (and (some file-formats/extractable-mime-types mimes)
                               (> (count mimes) 1))]
    (if conflicting-mimes
      (>evt [::toast-conflicting-mimes])
      (doseq [f files]
        (if (and (contains? file-formats/accepted-mime-types-for-attachments (keyword (.-type f)))
                 (< (.-size f) 104857600))
          (let [reader (js/FileReader.)]
            (set! (.-onload reader) (partial process-file-read target-id f))
            (.readAsArrayBuffer reader f))
          (>evt [::set-file-read-in-progress false]))))))

(defn handle-files [target-id _]
  (let [files (->> (.getElementById js/document file-element-id)
                   .-files
                   array-seq)]
    (read-files target-id files)))

(defn process-version-file-read [app-id att-id file event]
  (comm/send-command-execution
    {:command         :attachment/initialize-upload
     :data            {:content-type  (.-type file)
                       :md5-digest    (-> event .-target .-result js/Uint8Array. md5)
                       :attachment-id att-id}
     :on-success      [::start-file-version-upload app-id att-id file]
     :on-server-error [::file-version-upload-failure att-id]}))

(defn handle-file-version-upload [app-id att-id _]
  (p/if-all-let [elem (.getElementById js/document (file-version-element-id att-id))
                 file (->> elem
                           .-files
                           array-seq
                           first)]
    (when (and (contains? file-formats/accepted-mime-types-for-attachments (keyword (.-type file)))
               (< (.-size file) 104857600))
      (>evt [::version-add-started att-id])
      (set! (.-value elem) "") ; Have to reset the input value so that next change is detected properly
      (let [reader (js/FileReader.)]
        (set! (.-onload reader) #(process-version-file-read app-id att-id file %))
        (.readAsArrayBuffer reader file)))))

(defn stop-event [e]
  (.stopPropagation e)
  (.preventDefault e))

(defn- handle-file-drop [target-id e]
  (stop-event e)
  (p/if-all-let [dt    (.-dataTransfer e)
                 files (-> (.-files dt)
                           array-seq)]
    (read-files target-id files)))

(defn drag-over [e]
  (stop-event e)
  (-> e .-dataTransfer .-dropEffect (set! "copy")))

(defn save-attachments [uploads target-id property? bond?]
  (doseq [[_ up] uploads]
    (>evt [::create-attachment target-id up property? bond?])))

(defn format-size [size]
  (if (> size 1048576)
    (str (.toFixed (/ size 1048576) 2) " MiB")
    (str (.toFixed (/ size 1024) 1) " KiB")))

;; Subs
;; ====


(rf/reg-sub ::pending-uploads
  :-> ::pending-uploads)

(rf/reg-sub ::pending-uploads-new-files
  :<- [::pending-uploads]
  (fn [uploads _]
    (->> uploads
         (filter (comp nil? :attachment-id second)))))

(rf/reg-sub ::pending-uploads-new-versions
  :<- [::pending-uploads]
  (fn [uploads _]
    (->> uploads
         (filter (comp some? :attachment-id second)))))

(rf/reg-sub ::filename
  (fn [[_ attachment-id]]
    (rf/subscribe [::entities/entity attachment-id]))
  (fn [attachment _]
    (->> attachment
         :attachment/versions
         (sort-by :attachment.version/version)
         last
         :attachment.version/files
         first
         :attachment.version.file/filename)))

(rf/reg-sub ::pending-creates?
  (fn [db]
    (-> db ::pending-creates seq boolean)))

(rf/reg-sub
  ::all-uploads-valid
  :<- [::pending-uploads]
  (fn [uploads]
    (every? :valid? (vals uploads))))

(rf/reg-sub ::file-read-in-progress
  :-> ::file-read-in-progress)

(rf/reg-sub ::zip-read-in-progress
  :-> ::zip-read-in-progress)

(rf/reg-sub ::upload-metadata-input-active
  :<- [::pending-uploads]
  :<- [::zip-read-in-progress]
  (fn [[pending-uploads zip-read-in-progress?] _]
    (and (seq pending-uploads)
         (not zip-read-in-progress?))))

(rf/reg-sub ::user-fullname
  :<- [:session/user]
  (fn [user]
    (-> user user-model/full-name)))

;; Event handlers
;; ==============

(rf/reg-event-db
  ::set-pending-upload
  (fn [db [_ md5-hash file-node]]
    (assoc-in db [::pending-uploads md5-hash] file-node)))

(def cp-id-pattern #"_cp_(\d+)\.pdf$")

(defn connect-attachment-if-exists [{::entities/keys [entity-db]} target-id {:keys [filename] :as file}]
  (let [attachment-id (->> filename
                           (re-find cp-id-pattern)
                           second
                           futil/parse-int)]
    (if (-> entity-db
            (get attachment-id)
            :entity/parent
            (= target-id))
      (-> file
          (update :filename #(str/replace % cp-id-pattern ".pdf"))
          (assoc :attachment-id attachment-id))
      file)))

(rc/reg-chain-named
  ::zip-extracted
  (fn [{:keys [db]} _]
    {:db (dissoc db ::pending-uploads)})
  ::set-files
  (fn [{:keys [db]} [_ target-id _ {:keys [attachments]}]]
    (doseq [attachment attachments]
      (let [md5-hash (-> attachment :object-key md5)]
        (>evt [::set-pending-upload
               md5-hash
               (->> {:md5-base64      md5-hash
                     :file            nil
                     :upload-progress 100
                     :file-id         (:object-key attachment)
                     :valid?          (:object-key attachment)
                     :content-type    (:mime attachment)}
                    (merge (select-keys attachment [:filename
                                                    :size
                                                    :attachment/types
                                                    :attachment/contents
                                                    :attachment/drawing-number]))
                    (connect-attachment-if-exists db target-id)
                    set-valid-attachment)])))
    {:db (assoc db ::zip-read-in-progress false)}))

(rf/reg-event-fx
  ::start-file-upload
  (fn [{:keys [db]} [_ target-id {:keys [md5-base64 content-type file]} _ {:keys [file-id upload-url]}]]
    (let [ajax-req (PUT upload-url {:headers          {"Content-Type" content-type}
                                    :body             file
                                    :handler          #(>evt [::upload-complete target-id md5-base64 file-id])
                                    :progress-handler #(>evt [::update-progress md5-base64 %])
                                    :error-handler    #(>evt [::upload-failure md5-base64 %])})]
      {:db (assoc-in db [::pending-uploads md5-base64 :ajax-request] ajax-req)})))

(rf/reg-event-fx
  ::start-file-version-upload
  (fn [_ [_ app-id att-id file _ {:keys [file-id upload-url]}]]
    (PUT upload-url {:headers {"Content-Type" (.-type file)}
                     :body    file
                     :handler #(>evt [::version-upload-complete
                                      app-id
                                      att-id
                                      file-id
                                      (.-name file)
                                      (.-type file)])})
    {}))

(defn calculate-progress [current-progress-in-db progress-event]
  (let [loaded (.-loaded progress-event)
        total  (.-total progress-event)]
    (when (every? number? [loaded total current-progress-in-db])
      (let [new-value (->> (/ loaded total)
                           (* 100)
                           int)
            min-value (if (and (= 0 current-progress-in-db)
                               (= 100 new-value))
                        ;; If previous call was still zero and now it's 100, this is a small file and waiting for ACK
                        ;; will take a proportionally longer time
                        50
                        90)]
        (->> new-value
             ;; This would report full progress before server ACKs, so we fake it a bit
             (min min-value))))))

(rf/reg-event-fx ::update-progress
  (fn [{:keys [db]} [_ md5-base64 progress-event]]
    (p/if-all-let [pending (get-in db [::pending-uploads md5-base64])
                   new-value (calculate-progress (:upload-progress pending) progress-event)]
      {:db (assoc-in db [::pending-uploads md5-base64 :upload-progress] new-value)})))

(rc/reg-chain-named
  ::upload-complete
  (fn [{:keys [db]} [_ _ md5-base64 file-id]]
    (if (get-in db [::pending-uploads md5-base64])
      {:db (-> db
               (assoc-in [::pending-uploads md5-base64 :file-id] file-id)
               (assoc-in [::pending-uploads md5-base64 :upload-progress] 100)
               (update-in [::pending-uploads md5-base64] set-valid-attachment))}
      {:db db}))
  ;;; When a zip file is uploaded, extract it immediately
  ::extract-zip-file
  (fn [{:keys [db]} [_ target-id]]
    (let [pending-uploads (::pending-uploads db)
          file            (first (vals pending-uploads))]
      (if (->> file
               :content-type
               keyword
               (contains? file-formats/extractable-mime-types))
        {:comm/command {:command    :attachment/extract-zip-file
                        :data       {:file-id   (:file-id file)
                                     :target-id target-id}
                        :on-success [::zip-extracted target-id]
                        :on-done    [::zip-fail]}}
        {}))))

(def upload-fail-notification [:notifications/add :toast :error :error/file-upload-title :error/file-upload-caption])

(defn log-upload-error [status-or-error server-error]
  (if server-error
    (js/console.error "File upload initialization failure:" status-or-error (pr-str server-error))
    (js/console.error "File upload storage PUT failure:" (pr-str status-or-error))))

(rf/reg-event-fx ::upload-failure
  (fn [{:keys [db]} [_ md5-base64 status-or-error server-error]]
    (log-upload-error status-or-error server-error)
    {:db       (assoc-in db [::pending-uploads md5-base64 :error] true)
     :dispatch upload-fail-notification}))

(rf/reg-event-fx ::file-version-upload-failure
  (fn [_ [_ attachment-id status-or-error server-error]]
    (log-upload-error status-or-error server-error)
    {:fx [[:dispatch upload-fail-notification]
          [:dispatch [::version-add-done attachment-id]]]}))

(rf/reg-event-fx
  ::version-upload-complete
  (fn [{:keys [db]} [_ app-id att-id file-id filename content-type]]
    (let [markup? (-> db ::markup?)]
      {:dispatch [::process/signal
                  app-id
                  [:process.signal/attachment-add-version
                   (merge {:attachment-id att-id
                           :file-id       file-id
                           :filename      filename
                           :content-type  content-type}
                          (when markup?
                            {:authority-markup? true}))]
                  {:on-done [::version-add-done att-id]}]})))

(rf/reg-event-db ::version-add-done
  (fn [db [_ att-id]]
    (-> db
        (update ::version-upload-in-progress disj att-id)
        (dissoc ::markup?))))

(rf/reg-event-db ::version-add-started
  (fn [db [_ att-id]]
    (update db ::version-upload-in-progress (comp set conj) att-id)))

(rf/reg-event-db ::remember-markup
  (fn [db _]
    (assoc db ::markup? true)))

(rf/reg-sub ::version-add-in-progress
  (fn [db _]
    (-> db ::version-upload-in-progress)))

(rf/reg-sub ::version-add-in-progress?
  :<- [::version-add-in-progress]
  (fn [in-progress [_ att-id]]
    (contains? in-progress att-id)))

(rf/reg-sub ::markup?
  (fn [db _]
    (-> db ::markup?)))

(rf/reg-event-fx
  ::file-added
  (fn [{:keys [db]} [_ target-id {:keys [content-type md5-base64] :as file}]]
    (let [mime (keyword content-type)]
      {:db           (-> (assoc-in db
                                   [::pending-uploads md5-base64]
                                   (connect-attachment-if-exists db target-id file))
                         (assoc ::file-read-in-progress false)
                         (assoc ::zip-read-in-progress (if (contains? file-formats/extractable-mime-types mime) true false)))
       :comm/command {:command         :attachment/initialize-upload
                      :data            {:content-type content-type
                                        :md5-digest   md5-base64}
                      :on-success      [::start-file-upload target-id file]
                      :on-server-error [::upload-failure md5-base64]}})))

(rf/reg-event-db ::set-create-as-new
  (fn [db [_ md5-base64 value]]
    (-> db
        (assoc-in [::pending-uploads md5-base64 :create-as-new?] value)
        (update-in [::pending-uploads md5-base64] set-valid-attachment))))

(rf/reg-event-fx ::create-attachment
  (fn [{:keys [db]} [_ target-id attachment property? bond?]]
    (let [add-version?  (and (:attachment-id attachment)
                             (-> attachment :create-as-new? not))
          md5           (:md5-base64 attachment)
          db-update     (update db ::pending-creates conj md5)
          version-attrs (select-keys attachment [:attachment-id
                                                 :file-id
                                                 :filename
                                                 :content-type])
          new-attrs     (merge (when property?
                                 {:property-id target-id})
                               (when bond?
                                 {:bond-id target-id})
                               (select-keys attachment [:attachment/types
                                                        :attachment/contents
                                                        :attachment/drawing-number
                                                        :file-id
                                                        :filename
                                                        :content-type
                                                        :restrict?]))]
      (cond
        bond?
        {:db           db-update
         :comm/command {:command          :bond/create-attachment
                        :method           :post
                        :data             new-attrs
                        :on-success       [::create-bond-attachment.success md5 target-id]
                        :on-error         [::create-attachment.done md5]
                        :finally-dispatch [::create-attachment.done md5]}}
        property?
        {:db           db-update
         :comm/command {:command          :property/create-attachment-with-version
                        :method           :post
                        :data             new-attrs
                        :on-success       [::create-property-attachment.success md5 target-id]
                        :on-error         [::create-attachment.done md5]
                        :finally-dispatch [::create-attachment.done md5]}}
        :else
        {:db       db-update
         :dispatch [::process/signal
                    target-id
                    (if add-version?
                      [:process.signal/attachment-add-version version-attrs]
                      [:process.signal/attachment-create-with-version new-attrs])
                    {:on-success [::create-attachment.success md5]
                     :on-error   [::create-attachment.done md5]
                     :on-done    [::create-attachment.done md5]}]}))))

(rf/reg-event-db ::create-attachment.success
  (fn [db [_ md5 _]]
    (p/dissoc-in db [::pending-uploads md5])))

(rf/reg-event-fx ::create-property-attachment.success
  (fn [{:keys [db]} [_ md5 property-id _]]
    {:comm/command {:command    :property/attachments
                    :method     :get
                    :data       {:property-id property-id}
                    :on-success [::entities/merge-entity-db]}
     :db           (p/dissoc-in db [::pending-uploads md5])}))

(rf/reg-event-fx ::create-bond-attachment.success
  (fn [{:keys [db]} [_ md5 bond-id _]]
    {:fx [[:dispatch [::bond-common/get-bond-attachments bond-id]]]
     :db (p/dissoc-in db [::pending-uploads md5])}))

(rf/reg-event-db ::create-attachment.done
  (fn [db [_ md5 _]]
    (update db ::pending-creates (fn [c] (remove #(= md5 %) c)))))

(def interceptors [(rf/inject-cofx :tr)])

(rf/reg-event-fx
  ::toast-conflicting-mimes
  interceptors
  (fn [_]
    {:dispatch-n [[:notifications/add
                   :toast
                   :error
                   :attachments/mime-conflict
                   :attachments/conflict-info]
                  [::cancel-upload]
                  [::set-file-read-in-progress false]]}))

(rf/reg-event-db
  ::data-entered
  (fn [db [_ md5-base64 field value]]
    (-> db
        (assoc-in [::pending-uploads md5-base64 field] value)
        (update-in [::pending-uploads md5-base64] set-valid-attachment))))

(rf/reg-event-db
  ::set-file-read-in-progress
  (fn [db [_ in-progress?]]
    (assoc db ::file-read-in-progress in-progress?)))

(rf/reg-event-fx
  ::zip-fail
  (fn [{:keys [db]} [_ status body]]
    {:comm/response {:response [status body]}
     :db            (-> (assoc db ::file-read-in-progress false)
                        (assoc ::zip-read-in-progress false)
                        (assoc ::pending-uploads {}))}))

(rf/reg-event-db
  ::cancel-upload
  (fn [db [_ maybe-md5]]
    (doseq [upload (if maybe-md5
                     [(get-in db [::pending-uploads maybe-md5])]
                     (-> db ::pending-uploads vals))]
      (when-let [req (:ajax-request upload)]
        (ajax/abort req)))
    (if maybe-md5
      (p/dissoc-in db [::pending-uploads maybe-md5])
      (assoc db ::pending-uploads {}))))

;; Reagent components
;; ==================

(defn progress-bar [amount]
  [:div.file-progress-bar
   [:div.progress-color {:style {:width (str amount "%")}}]
   [:div.progress-text {:style {:color (if (< amount 50) "black" "white")}}
    (str amount " %")]])

(defn error-bar [text]
  [:div.file-progress-bar.bg-ui-red-110
   [:div.progress-text.text-ui-white
    text]])

(defn attachment-type-select [{:keys [tr file-data md5 property? bond?]}]
  (if (<sub [::ws-subs/loading-config?])
    [:div.mt-1 (cc/select-skeleton {})]
    (let [type-mapping-fn  (fn [[text id]]
                             {:id id :label text})
          available-types  (cond
                             property?
                             (->> (<sub [::property-subs/attachment-types-with-i18n])
                                  (map type-mapping-fn))
                             bond?
                             (->> (<sub [::bond-subs/attachment-types-with-i18n])
                                  (map type-mapping-fn))
                             :else
                             (->> (<sub [:app.front.view.application/attachment-types-with-i18n])
                                  (map type-mapping-fn)))
          attachment-types (:attachment/types file-data)]
      [:div.multi-select
       (cc/multi-select
         {:id                     (str md5 "-type")
          :title-text             (tr [:upload/type])
          :class-name             "attachment-type-multi-select"
          :data-cy                "attachment-type-multi-select"
          :label                  (attachment-common/types-str tr (map #(<sub [::entities/entity %]) attachment-types))
          :initial-selected-items []
          :items                  available-types
          :on-change              (fn [e]
                                    (let [values (attachment-common/keywords-from-multi-select e)]
                                      (>evt [::data-entered md5 :attachment/types values])))})])))

(defn on-change-fn [md5 attr]
  (fn [ev]
    (>evt [::data-entered md5 attr (-> ev .-target .-value)])))

(defn metadata-text-input [{:keys [tr md5 attr file-data label]}]
  [ui/text-input {:id         (str md5 (name attr))
                  :label-text (tr [(or label attr)])
                  :value      (attr file-data)
                  :on-change  (on-change-fn md5 attr)}])

(defn visibility-details
  [{:keys [tr md5 file-data]}]
  [attachment-common/visibility-details
   {:tr          tr
    :owner-name  (<sub [::user-fullname])
    :restricted? (-> file-data :restrict? boolean)
    :on-click    (fn [restrict?] (>evt [::data-entered md5 :restrict? restrict?]))}])

(defn attachments-list [{:keys [attachments tr property? bond?]}]
  (when (seq attachments)
    (for-frag [[md5
                {:keys [filename
                        upload-progress
                        attachment-id
                        size
                        create-as-new?
                        error]
                 :as   file-data}] attachments]
      ^{:key md5}
      [:div.bg-ui-light-gray.p-4.mb-4
       [:div.flex.justify-between.w-full.items-center
        (let [old-name (<sub [::filename attachment-id])]
          [:div.flex-1
           (str (cond
                  (nil? attachment-id)
                  filename
                  (= old-name filename)
                  (tr [:upload/replace-file] [filename])
                  :else
                  (tr [:upload/replace-file-new-name] [filename old-name]))
                " (" (format-size size) ")")])
        (when (some? attachment-id)
          [:div.w-64
           [yes-no/yes-no-radio-buttons
            tr
            (str md5 "-as-new-version")
            {:value-selected (not create-as-new?)
             :on-change      #(>evt [::set-create-as-new md5 (not %)])
             :tr-no          :upload/no-as-new}]])
        [:div.w-16
         (if error
           [error-bar (tr [:error/error])]
           [progress-bar upload-progress])]
        [:span.controls.w-8
         (cc/icon {:icon        i/trash
                   :description (tr [:upload/remove-attachment])
                   :class-name  "cursor-pointer"
                   :on-click    #(>evt [::cancel-upload md5])})]]
       (when (or create-as-new? (nil? attachment-id))
         [:<>
          [:div.flex.flex-row.flex-wrap.w-full.gap-4
           [:div.w-full.md:w-1-3
            [attachment-type-select (util/mmap tr md5 file-data property? bond?)]]
           (when-not bond?
             [:div.w-full.md:w-40
              [metadata-text-input
               (util/mmap {:attr :attachment/drawing-number}
                          tr md5 file-data)]])
           [:div.flex-1
            [metadata-text-input
             (util/mmap {:attr  :attachment/contents
                         :label :upload/description}
                        tr md5 file-data)]]
           [:div.w-full
            (when-not (or bond? property?)
              [visibility-details
               (util/mmap tr md5 file-data)])]]])])))

(defn initial-attachment-metadata-input [{:keys [target-id property? bond?]}]
  (let [tr (<sub [:tr])]
    [:div.upload
     [attachments-list (util/mmap {:attachments (<sub [::pending-uploads-new-versions])}
                                  tr
                                  property?
                                  bond?)]
     [attachments-list (util/mmap {:attachments (<sub [::pending-uploads-new-files])}
                                  tr
                                  property?
                                  bond?)]
     [:div.buttons
      (cc/button {:kind             :primary
                  :icon             i/check
                  :icon-description ""
                  :disabled         (or (not (<sub [::all-uploads-valid]))
                                        (<sub [::pending-creates?]))
                  :on-click         #(save-attachments (<sub [::pending-uploads]) target-id property? bond?)}
                 (tr [:upload/done]))
      (cc/button {:kind             :tertiary
                  :icon             i/remove
                  :icon-description ""
                  :on-click         #(>evt [::cancel-upload])}
                 (tr [:cancel]))]]))

(defn upload-widget [{:keys [target-id]}]
  (let [tr                    (<sub [:tr])
        zip-read-in-progress? (<sub [::zip-read-in-progress])
        read-in-progress?     (<sub [::file-read-in-progress])]
    [:div.upload-area__drop-area
     {:on-drag-enter stop-event
      :on-drag-over  drag-over
      :on-drop       (partial handle-file-drop target-id)}
     (if (or read-in-progress?
             zip-read-in-progress?)
       [:div
        (cc/inline-loading {:description (tr [:upload/preparing-new-files])})]
       [:div.leading-tight
        [:input {:type       "file"
                 :id         file-element-id
                 :multiple   "multiple"
                 :style      {:display "none"}
                 :accept     (str/join "," file-formats/accept-value-for-input)
                 :on-change  (partial handle-files target-id)
                 :aria-label (tr [:file/upload])}]
        [:div.upload-area__drop-area__upper
         [:span (tr [:attachments/upload-text1])]
         [:br]
         [:span (cc/link {:href "#" :on-click open-filepicker}
                         (tr [:attachments/upload-link])) " " (tr [:attachments/upload-text2])]]
        [:div.upload-area__drop-area__lower.max-w-xl
         [:span (tr [:attachments/upload-text3])]]])]))

(defn upload-area [tr target property? bond?]
  (when (or (<sub [::process/signal-permitted?
                   target
                   [:process.signal/attachment-create-with-version]])
            property?
            bond?)
    (let [opts {:target-id (if (number? target)
                             target
                             (:db/id target))
                :property? property?
                :bond?     bond?}]
      [:div.upload-area
       (cond
         (<sub [::pending-creates?])
         (cc/inline-loading {:data-cy     "upload-loading"
                             :description (tr [:attachments/wait-for-scan])})
         (<sub [::upload-metadata-input-active])
         [initial-attachment-metadata-input opts]
         :else
         [upload-widget opts])])))

(defn upload-version-button [_]
  ;; separate atom allows the loading-finished-animation to run
  (let [show-loading (r/atom false)]
    (fn [{:keys [parent-id attachment-id markup?] :as props}]
      (when (<sub [::process/signal-permitted?
                   parent-id
                   [:process.signal/attachment-add-version {:attachment-id attachment-id}]])
        (let [tr           (<sub [:tr])
              add-ongoing? (<sub [::version-add-in-progress? attachment-id])]
          [:div.file-version-upload
           [:input {:type      "file"
                    :id        (file-version-element-id attachment-id)
                    :style     {:display "none"}
                    :accept    (str/join "," file-formats/accept-value-for-input)
                    :on-change (fn [e]
                                 (reset! show-loading true)
                                 (when markup?
                                   (>evt [::remember-markup]))
                                 (handle-file-version-upload parent-id
                                                             attachment-id
                                                             e))}]
           (if (or add-ongoing? @show-loading)
             (cc/inline-loading {:description (tr [:upload/uploading])
                                 :success     (not add-ongoing?)
                                 :on-success  (fn [_] (reset! show-loading false) true)})
             (cc/button
               (r/merge-props
                 {:kind     :tertiary
                  :icon     i/upload
                  :on-click #(open-filepicker (file-version-element-id attachment-id) %)}
                 (st/select-schema props cc/ButtonOptions))
               (if markup?
                 (tr [:upload/upload-markup-version])
                 (tr [:upload/upload-new-version]))))])))))
